import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, MenuItem, Select,
  FormControl, InputLabel, IconButton, Box, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../utilities/ModalStackContext';

const newRow = () => ({ type: 'car', value: '', amount: '', memo: '' });

export default function BulkPurchaseModal({ open, onClose }) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [vendor, setVendor] = React.useState('');
  const [isPayable, setIsPayable] = React.useState('true');
  const [paidDate, setPaidDate] = React.useState('');
  const [rows, setRows] = React.useState([newRow()]);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setVendor('');
      setIsPayable('true');
      setPaidDate('');
      setRows([newRow()]);
    }
  }, [open]);

  const handleIsPayableChange = (e) => {
    const val = e.target.value;
    setIsPayable(val);
    if (val === 'false') setPaidDate(moment().format('YYYY/MM/DD'));
    else setPaidDate('');
  };

  const updateRow = (index, field, value) => {
    setRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addRow = () => setRows(prev => [...prev, newRow()]);

  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!vendor.trim()) {
      StateManager.setAlertAndOpen('Vendor is required', 'warning');
      return;
    }
    const validRows = rows.filter(r => r.amount && !isNaN(parseFloat(r.amount)));
    if (validRows.length === 0) {
      StateManager.setAlertAndOpen('At least one row needs a valid amount', 'warning');
      return;
    }
    const missingEntity = validRows.some(r => r.type !== 'standalone' && !r.value.trim());
    if (missingEntity) {
      StateManager.setAlertAndOpen('All rows need an entity value or set type to Standalone', 'warning');
      return;
    }

    setSaving(true);
    try {
      const db = firebase.firestore();
      const batch = db.batch();
      const base = {
        date: moment().format('YYYY/MM/DD'),
        vendor: vendor.trim(),
        isPayable: isPayable === 'true',
        ...(isPayable === 'false' ? { paidDate } : {}),
      };
      validRows.forEach(row => {
        const ref = db.collection('purchases').doc();
        const doc = {
          ...base,
          amount: parseFloat(row.amount),
          memo: row.memo.trim(),
        };
        if (row.type === 'car') doc.stock = row.value.trim();
        if (row.type === 'so') { doc.stock = row.value.trim(); doc.type = 'service'; }
        batch.set(ref, doc);
      });
      await batch.commit();
      const count = validRows.length;
      StateManager.setAlertAndOpen(`${count} purchase${count > 1 ? 's' : ''} created`, 'success');
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to save purchases', 'error');
    }
    setSaving(false);
  };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Bulk Add Purchases</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Shared fields */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Vendor"
              value={vendor} onChange={e => setVendor(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Paid?</InputLabel>
              <Select value={isPayable} onChange={handleIsPayableChange} label="Paid?" MenuProps={selectMenuProps}>
                <MenuItem value="false">Yes (Paid)</MenuItem>
                <MenuItem value="true">No (Unpaid)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Rows table */}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 130 }}>Type</TableCell>
              <TableCell>Stock / SO #</TableCell>
              <TableCell sx={{ width: 120 }}>Amount</TableCell>
              <TableCell>Memo</TableCell>
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <FormControl fullWidth size="small">
                    <Select value={row.type} onChange={e => updateRow(i, 'type', e.target.value)} MenuProps={selectMenuProps}>
                      <MenuItem value="car">Car</MenuItem>
                      <MenuItem value="so">Service Order</MenuItem>
                      <MenuItem value="standalone">Standalone</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField
                    fullWidth size="small"
                    placeholder={row.type === 'so' ? 'SO number' : row.type === 'standalone' ? '—' : 'Stock number'}
                    value={row.value}
                    disabled={row.type === 'standalone'}
                    onChange={e => updateRow(i, 'value', e.target.value)}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField
                    fullWidth size="small" type="number" placeholder="0.00"
                    value={row.amount}
                    onChange={e => updateRow(i, 'amount', e.target.value)}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 1 }}>
                  <TextField
                    fullWidth size="small" placeholder="Memo"
                    value={row.memo}
                    onChange={e => updateRow(i, 'memo', e.target.value)}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.75, px: 0.5 }}>
                  <IconButton size="small" onClick={() => removeRow(i)} disabled={rows.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={addRow}
          sx={{ mt: 1.5 }}
        >
          Add Row
        </Button>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 2 }}>
          Will create {rows.filter(r => r.amount).length} purchase record{rows.filter(r => r.amount).length !== 1 ? 's' : ''}
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
